/**
 * Feature E03-F02-T04 — Project DETAIL view (the host that MOUNTS the per-stage
 * bodies AND the document-package dialogs into the running app).
 *
 * The review flagged that `PreKickoffPanel` (E03-F02-T04) and `StageStepper`
 * (E02-F02-T04) were built but never mounted anywhere reachable — the SPA shell
 * only rendered placeholders for `view === 'project'`. This component closes that
 * gap: it is the `project` detail body wired into `shell/AppShell.tsx`, fetches
 * the project's detail card (`useProjectDetail` → GET /projects/:id), and renders:
 *   - the 3-column header (role / client / contact + status badge — projected by
 *     `projectDetailModel.headerFields`);
 *   - the 4-step `StageStepper` (current-stage highlight + preview banner);
 *   - the per-stage body:
 *     * Stage-1 (Pre-Kickoff)  → `PreKickoffPanel`
 *     * Stage-2 (Kick-off)     → `KickoffView` (questions + notes + Complete Meeting)
 *     * Stage-3 (Generation)   → `GenerationStageView` (doc-package dashboard)
 *     * Stage-4 (Launched)     → `LaunchedStageView` (Package Ready for Delivery —
 *                                the final document package, read-only + download)
 *   - Back / Next stage-advance controls (legacy `advanceStage`);
 *   - the `ProjectTimeline` (event history) section.
 *
 * TIER-1 wiring (2026-07): the Generation stage now supplies every callback the
 * `GenerationStageView` exposes — View/Edit opens the `DocumentEditor`, Send /
 * Send-to-Sourcing open the `SendEmailDialog`, Send-Notification opens the
 * `InternalNotificationDialog`, Download exports the `.doc`, and "Confirm & Move
 * to Final" calls `confirm_package` then advances the stage. Each stage body is
 * wrapped in `SectionErrorBoundary` so a crash only shows an inline error.
 */
import { useCallback, useEffect, useState, type JSX } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  MAX_NAV_STAGE,
  MIN_STAGE,
  STAGE_NAMES,
  GENERATION_LABELS,
  isConfirmRequired,
  attachedDocumentNote,
  exportLabel,
  finalPackageBody,
  shouldAttachDocument,
  followupDefaultCc,
  stripFollowupSignaturePlaceholder,
  type DetailProject,
  type EditableDocType,
  type Stage5ItemKey,
  type ProjectSourcingTeamRef,
  type PreKickoffDraftOption,
  type EmailRecipientsMap,
} from '@eer/shared';
import {
  useProjectDetail,
  useUpdateProject,
  useAdvanceStage,
  discardProjectDraft,
} from './projectDetailApi.js';
import {
  cancelScheduledDiscard,
  forgetFreshDraft,
  isFreshDraft,
  scheduleDraftDiscard,
} from './freshDraftRegistry.js';
import { qk } from '../api/queryKeys.js';
import { errorBodyOf, errorMessage } from '../api/client.js';
import { getCachedUser } from '../api/session.js';
import { readKickoffEnrichment } from '../kickoff/kickoffEnrichmentModel.js';
import { useConfirm } from '../ui/ConfirmDialog.js';
import { headerFields, stageToStepIndex } from './projectDetailModel.js';
import { ProjectDetailHeader } from './ProjectDetailHeader.js';
import { SourcingTeamCard } from './SourcingTeamCard.js';
import { StageStepper } from './StageStepper.js';
import { PreKickoffPanel } from '../prekickoff/PreKickoffPanel.js';
import { ProjectContactsCard } from '../contacts/ProjectContactsCard.js';
import { directoryKeys, fetchProjectContacts } from '../contacts/directoryApi.js';
import { readValidationFields, useSendPreKickoffDraft } from '../prekickoff/prekickoffApi.js';
import type { DraftEditorState, PersistedArtifactRow } from '../prekickoff/prekickoffModel.js';
import { KickoffView } from '../kickoff/KickoffView.js';
import { readSavedKickoffText } from '../kickoff/kickoffModel.js';
import { useProjectKickoffQuestions } from '../kickoff/kickoffApi.js';
import { GenerationStageView } from '../document/GenerationStageView.js';
import { DocumentEditor } from '../document/DocumentEditor.js';
import { DocumentView } from '../document/DocumentView.js';
import { SendEmailDialog } from '../document/SendEmailDialog.js';
import { InternalNotificationDialog } from '../document/InternalNotificationDialog.js';
import {
  internalNotificationDefaultsQuery,
  useConfirmPackage,
} from '../document/docGenerationApi.js';
import { downloadArtifactDoc } from '../document/docExport.js';
import {
  applyNotifTemplate,
  artifactStatusOf,
  badgeFor,
  GENERATION_CARDS,
  isCommunicationItem,
  splitTemplate,
} from '../document/docGenerationModel.js';
import type { ArtifactLike, ArtifactMap } from '../document/docGenerationModel.js';
import { LaunchedStageView } from '../document/LaunchedStageView.js';
import { ProjectTimeline } from '../projects/ProjectTimeline.js';
import { Dialog } from '../ui/Dialog.js';
import { useToast } from '../ui/ToastContext.js';
import { SectionErrorBoundary } from '../ui/ErrorBoundary.js';

export interface ProjectDetailViewProps {
  /** Project id from the route (`/project/:id`); null when none is open. */
  readonly projectId: number | null;
  /**
   * E12-F17-T15 — leave the card back to the project list (the shell's guarded
   * navigate). Used after Cancel discards a fresh "New Project" draft.
   */
  readonly onClose?: () => void;
}

/**
 * PK-QA-8 — human labels for the hard-missing invariant fields the stage-advance
 * 400 `VALIDATION_ERROR` reports (`client_name` / `role_name` / `contact_email`)
 * when advancing Pre-Kickoff(1)→Kick-off(2). Mirrors `PreKickoffPanel`'s
 * `MISSING_FIELD_LABELS` so the toast copy for this invariant is identical whether
 * it is raised by the AI-analysis 400 or the advance 400 — these fields have NO
 * confirm path (the user must fill them in first).
 */
const MISSING_FIELD_LABELS: Record<string, string> = {
  client_name: 'Company',
  role_name: 'Position',
  contact_email: 'Contact email',
};

/**
 * PK-QA-20 — the stage-advance route answers a HARD, non-overridable HTTP 400
 * `{ error: { code:'KICKOFF_INCOMPLETE', fields } }` on a Kick-off(2)→Generation(3)
 * attempt when the meeting is not completed and/or no AI Processing (kickoff
 * enrichment) has run yet. `fields` is a subset of
 * `['meeting_completed','kickoff_enrichment']` naming which condition is unmet.
 * Unlike the soft `no_analysis_yet` 412 there is NO confirm/override path — the
 * caller just blocks + notifies. The code is DISTINCT from the 1→2 gate's
 * `VALIDATION_ERROR` so this 2→3 toast never collides with the pre-kickoff
 * missing-field highlight (same `{ error: { code, fields } }` envelope family, so
 * it reuses the same `errorBodyOf` body). Returns the unmet-field list, or `null`
 * when `body` is not this envelope (so the caller falls through to other branches).
 */
function readKickoffIncompleteFields(body: unknown): string[] | null {
  if (!body || typeof body !== 'object') return null;
  const err = (body as { error?: unknown }).error;
  if (!err || typeof err !== 'object') return null;
  const { code, fields } = err as { code?: unknown; fields?: unknown };
  if (code !== 'KICKOFF_INCOMPLETE') return null;
  return Array.isArray(fields) ? fields.filter((f): f is string => typeof f === 'string') : [];
}

/** Which detail-project fields the Generation stage callbacks read. */
interface GenerationProjectMeta {
  readonly clientName: string;
  readonly roleName: string;
  /**
   * The client contact's email (`project.contact_email`) — pre-filled into the
   * send dialog's `To` for the client-facing follow-up email (PK-QA-28). Empty
   * string when the project has no contact email.
   */
  readonly contactEmail: string;
}

/**
 * Map a Generation card doc type to its universal Stage-5 `item_key` for the send
 * dialog. Only the types with a real send endpoint are mapped; `inmails` has no
 * Stage-5 send (they are copied/downloaded, not emailed) → null.
 */
function sendItemKeyFor(docType: string): Stage5ItemKey | null {
  switch (docType) {
    case 'client_followup_email':
      return 'client_followup_email';
    case 'sourcing_sheet':
      return 'sourcing_sheet_email';
    case 'kickoff_sheet':
      return 'kickoff_sheet';
    default:
      return null;
  }
}

/**
 * PK-QA-26 — build the FULL snake_case notification template-variable set
 * CLIENT-SIDE (variant (a) of the ADR — no `packages/api` change), mirroring the
 * server's (currently dead) `buildNotifTemplateVars` so the `int_notif_standard` /
 * `int_notif_confidential` templates substitute real values instead of the old
 * two-key camelCase object (which left `{{client_name}}` … all `''`).
 *
 * Keys the templates use: `client_name`, `role_name`, `country`, `kickoff_date`,
 * `salary`, `sender_name`, `contact_name`, `contact_email`. Every source is
 * already in scope of `ProjectDetailView`:
 *   - client_name / role_name  ← the resolved header meta;
 *   - contact_name / contact_email ← `project.*` (contacts JOIN);
 *   - country ← kickoff_enrichment.country ?? .location (projects.data);
 *   - salary ← kickoff_enrichment.salary ?? .compensation ?? 'TBC' (the server's
 *     explicit fallback);
 *   - kickoff_date ← today, dd/mm/yyyy (en-GB), matching the legacy SPA;
 *   - sender_name ← the signed-in user's name (cached session identity).
 * A field with no source resolves to '' (parity with the server's `str(...)`).
 */
function buildNotifTemplateVarsClient(
  project: DetailProject,
  clientName: string,
  roleName: string,
  senderName: string,
  now: Date = new Date(),
): Record<string, string> {
  const ke = readKickoffEnrichment(project.data) ?? {};
  const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
  const firstNonEmpty = (...vals: string[]): string => vals.find((v) => v.trim() !== '') ?? '';
  const country = firstNonEmpty(str(ke['country']), str(ke['location']));
  const salary = firstNonEmpty(str(ke['salary']), str(ke['compensation'])) || 'TBC';
  const dd = String(now.getDate()).padStart(2, '0');
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const kickoffDate = `${dd}/${mm}/${now.getFullYear()}`;
  return {
    client_name: clientName,
    role_name: roleName,
    country,
    kickoff_date: kickoffDate,
    salary,
    sender_name: senderName,
    contact_name: project.contact_name ?? '',
    contact_email: project.contact_email ?? '',
  };
}

// ---------------------------------------------------------------------------
// Stage-2: Kick-off body
// ---------------------------------------------------------------------------

function KickoffStageBody({
  projectId,
  projectRoleName,
  projectData,
}: {
  projectId: number;
  projectRoleName?: string | null;
  /** projects.data — the saved notes/transcript seed the editors (E02-F05-T09). */
  projectData?: Record<string, unknown> | null;
}): JSX.Element {
  const query = useProjectKickoffQuestions(projectId);

  if (query.isPending) {
    return (
      <div data-testid="kickoff-loading" className="p-6 text-sm text-text-muted">
        <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
        Loading questionnaire…
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div
        data-testid="kickoff-load-error"
        className="rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-700"
      >
        Failed to load the kick-off questionnaire. Please refresh or contact support.
      </div>
    );
  }

  // E02-F05-T09 — without these the editors always opened empty and the next
  // autosave could write that emptiness over the saved meeting notes.
  const saved = readSavedKickoffText(projectData);
  return (
    <KickoffView
      projectId={projectId}
      initialQuestions={query.data}
      initialNotes={saved.notes}
      initialTranscript={saved.transcript}
      projectRoleName={projectRoleName}
    />
  );
}

// ---------------------------------------------------------------------------
// Stage-3: Generation body — owns the editor + send + notification dialogs
// ---------------------------------------------------------------------------

/** State of the send-email dialog: which item + the pre-composed draft. */
interface SendState {
  readonly itemKey: Stage5ItemKey;
  readonly title: string;
  readonly to: string;
  readonly subject: string;
  readonly body: string;
  readonly cc: readonly string[];
  /** E05-F06-T11 — blind copies (restored from the last successful send). */
  readonly bcc?: readonly string[];
}

function GenerationStageBody({
  projectId,
  meta,
  artifactMap,
  projectData,
  generationNotes,
  sourcingTeamId,
  sourcingTeam,
  savedRecipients,
  notifTemplateVars,
  notificationSentAt,
  onSaveGenerationNotes,
  onAdvanceStage,
}: {
  projectId: number;
  meta: GenerationProjectMeta;
  artifactMap: ArtifactMap;
  /** `projects.data` — source of the follow-up CC default (`cc_emails` + `contact_cc`). */
  projectData: Readonly<Record<string, unknown>>;
  generationNotes: string;
  sourcingTeamId: number | null;
  sourcingTeam: ProjectSourcingTeamRef | null;
  /** KS3-05 — addressees remembered from the previous send, keyed by item. */
  savedRecipients: EmailRecipientsMap;
  /** Full snake_case template-var set for the Internal Notification dialog (PK-QA-26). */
  notifTemplateVars: Readonly<Record<string, string>>;
  /** E03-F07-T07 — `project.internal_notification_sent_at` («Kickoff done mail» Sent). */
  notificationSentAt: string | null;
  onSaveGenerationNotes: (notes: string) => void;
  onAdvanceStage: (stage: number) => Promise<void>;
}): JSX.Element {
  const toast = useToast();
  const confirmPackage = useConfirmPackage(projectId);
  // KS3-03 — the same defaults entry the Internal Notification dialog uses (shared
  // query key, 60 s staleTime, so this costs one cached GET). It carries the
  // `sourcing_team_email` template "Send to Sourcing Team" pre-composes with.
  // Read through `ensureQueryData` at click time, NOT from a hook snapshot: a click
  // that beats the in-flight fetch would otherwise fall back to the bare document
  // with no sign that anything was skipped.
  const queryClient = useQueryClient();

  const [editorType, setEditorType] = useState<EditableDocType | null>(null);
  const [send, setSend] = useState<SendState | null>(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const artifactOf = useCallback(
    (type: string): ArtifactLike | undefined => artifactMap[type],
    [artifactMap],
  );

  /** Open the send dialog for a comm doc (pre-compose To/Subject/Body). */
  const openSend = useCallback(
    async (docType: string) => {
      const itemKey = sendItemKeyFor(docType);
      if (!itemKey) {
        toast.error('This document cannot be emailed');
        return;
      }
      const content = (artifactOf(docType)?.content ?? {}) as Record<string, unknown>;
      const subject = typeof content['subject'] === 'string' ? content['subject'] : '';
      const rawBody = typeof content['body'] === 'string' ? content['body'] : '';
      // E03-F04-T03 #4 — the follow-up body must not carry an AI-written sign-off:
      // the recruiter signature (users.email_signature) is appended by the dialog
      // preview and by the sender. Artifacts generated before the prompt rule may
      // still end with `[Your Name]` & co.
      const body =
        docType === 'client_followup_email' ? stripFollowupSignaturePlaceholder(rawBody) : rawBody;
      // KS3-03 — the sheet body lives under `kickoff_sheet`, with `text` only as the
      // older shape; reading `text` alone left "Send to Sourcing Team" with an EMPTY
      // body on every real project (found live on dev, project 201). Same precedence
      // as docViewModel / docRender, which have always read both.
      const kickoffText =
        typeof content['kickoff_sheet'] === 'string'
          ? content['kickoff_sheet']
          : typeof content['text'] === 'string'
            ? content['text']
            : '';
      // E05-F06-T10 — the sheet now travels as a real `.docx` (B1b/KS-17), so the
      // body must POINT at the file instead of repeating it. Before the attachment
      // existed, pasting the document in was the ONLY way to deliver it; keeping that
      // paste made the recipient read the same sheet twice — badly reflowed in the
      // message and again in the attachment. `shouldAttachDocument` is the same
      // predicate the sender uses, so body and attachment can never disagree about
      // who carries the document.
      const docInBody = shouldAttachDocument(itemKey)
        ? attachedDocumentNote(exportLabel(docType))
        : kickoffText;
      // KS3-05 — the set remembered from the last successful send of THIS item wins
      // over the one-address default. Without it every repeat send started from the
      // single project contact and the recruiter re-typed the rest.
      const saved = savedRecipients[itemKey];
      const savedTo = (saved?.to ?? []).filter((e) => e.trim().length > 0);
      const savedCc = (saved?.cc ?? []).filter((e) => e.trim().length > 0);
      const savedBcc = (saved?.bcc ?? []).filter((e) => e.trim().length > 0);
      const to =
        savedTo.length > 0
          ? savedTo.join(', ')
          : docType === 'client_followup_email'
            ? meta.contactEmail
            : '';
      // E03-F04-T03 #5 — the follow-up CC defaults to the union of data.cc_emails
      // (header CC of the intake email), data.contact_cc (CC from the intro) and the
      // project's CC contacts, deduplicated by lowercase. Read at click time via
      // ensureQueryData (same reason as the sourcing template below); a failed
      // contacts read degrades to the two data lists.
      let cc: readonly string[] = savedCc;
      if (savedCc.length === 0 && docType === 'client_followup_email') {
        const contactCc = await queryClient
          .ensureQueryData({
            queryKey: directoryKeys.projectContacts(projectId),
            queryFn: () => fetchProjectContacts(projectId),
          })
          .then((r) => (Array.isArray(r?.cc) ? r.cc.map((c) => c.email) : []))
          .catch(() => [] as (string | null)[]);
        cc = followupDefaultCc(projectData, contactCc, to.split(','));
      }
      // KS3-03 — the sourcing-team send opens from a template instead of the bare
      // document. Only for `kickoff_sheet` (the "Send to Sourcing Team" action the
      // client named); the document itself is substituted into {{kickoff_sheet}}, so
      // nothing is lost if the template is edited down to that single token. An
      // absent/blank row (migration not applied, template cleared in Admin) leaves
      // `tpl` empty and the previous raw-document composition stands.
      // E12-F08-T01 — the Sourcing Sheet send reads its OWN row
      // (`sourcing_sheet_team_email`, field `template_sourcing_sheet`) the same way;
      // the attachment pointer goes into {{sourcing_sheet}}. The kickoff wording talks
      // about the kickoff sheet and kick-off date, so it is never reused here.
      const tplField =
        docType === 'kickoff_sheet'
          ? 'template_sourcing_team'
          : docType === 'sourcing_sheet'
            ? 'template_sourcing_sheet'
            : null;
      const tplRaw = tplField
        ? await queryClient
            .ensureQueryData(internalNotificationDefaultsQuery(projectId))
            .then((d) => d[tplField] ?? '')
            .catch(() => '')
        : '';
      const tpl = tplRaw.trim()
        ? splitTemplate(
            applyNotifTemplate(tplRaw, {
              ...notifTemplateVars,
              [docType === 'sourcing_sheet' ? 'sourcing_sheet' : 'kickoff_sheet']: docInBody,
            }),
          )
        : null;
      setSend({
        itemKey,
        title:
          docType === 'client_followup_email'
            ? 'Follow-up Email'
            : docType === 'kickoff_sheet'
              ? 'Kickoff Sheet to Sourcing Team'
              : docType === 'sourcing_sheet'
                ? 'Sourcing Sheet to Sourcing Team'
                : 'Send Email',
        // PK-QA-28: pre-fill To with the client contact's email ONLY for the
        // client-facing follow-up email. The team-facing items (kickoff_sheet /
        // sourcing_sheet_email) keep their team-based To seeding (handled inside
        // SendEmailDialog via initialSourcingTeam) — do not seed a client address there.
        to,
        subject:
          subject ||
          tpl?.subject ||
          (docType === 'kickoff_sheet'
            ? `Kickoff Sheet: ${meta.clientName} - ${meta.roleName}`
            : docType === 'sourcing_sheet'
              ? // E05-F06-T09 — the Sourcing Sheet send is team-facing, not a client
                // follow-up; the generic `Follow-up: <role>` subject would have been
                // simply wrong on the sourcers' side.
                `Sourcing Sheet: ${meta.clientName} - ${meta.roleName}`
              : `Follow-up: ${meta.roleName}`),
        body: body || tpl?.body || docInBody,
        cc,
        bcc: savedBcc,
      });
    },
    [
      artifactOf,
      meta.clientName,
      meta.roleName,
      meta.contactEmail,
      notifTemplateVars,
      projectData,
      projectId,
      queryClient,
      savedRecipients,
      toast,
    ],
  );

  const onDownload = useCallback(
    (docType: string) => {
      const ok = downloadArtifactDoc({
        docType,
        content: artifactOf(docType)?.content ?? null,
        clientName: meta.clientName,
        roleName: meta.roleName,
      });
      if (!ok) toast.error('No content to download');
    },
    [artifactOf, meta.clientName, meta.roleName, toast],
  );

  const onConfirmPackage = useCallback(async () => {
    setConfirming(true);
    try {
      await confirmPackage.mutateAsync();
      // Legacy confirmDocumentPackage then advances the project to Final (stage 4).
      await onAdvanceStage(4);
      toast.success('Document package confirmed — moved to Final');
    } catch {
      toast.error('Failed to confirm the document package');
    } finally {
      setConfirming(false);
    }
  }, [confirmPackage, onAdvanceStage, toast]);

  const editorArtifact = editorType ? artifactOf(editorType) : undefined;

  return (
    <>
      {/* Persistent sourcing-team assignment (E03-F08-T01) — survives reload,
          independent of the ephemeral "Send to Sourcing Team" email picker. */}
      <div className="mb-4">
        <SourcingTeamCard
          projectId={projectId}
          assignedTeamId={sourcingTeamId}
          assignedTeam={sourcingTeam}
        />
      </div>

      <GenerationStageView
        projectId={projectId}
        artifacts={artifactMap}
        generationNotes={generationNotes}
        onSaveGenerationNotes={onSaveGenerationNotes}
        onOpenEditor={(type) => setEditorType(type as EditableDocType)}
        onSendNotification={() => setNotifyOpen(true)}
        notificationSentAt={notificationSentAt}
        onDownload={onDownload}
        onSend={(type) => void openSend(type)}
        onSendToSourcing={() => void openSend('kickoff_sheet')}
        onConfirmPackage={onConfirmPackage}
        confirming={confirming}
      />

      {/* Document editor / preview (mounted in the shared Dialog shell). */}
      <Dialog
        open={editorType !== null}
        onClose={() => setEditorType(null)}
        title="Document"
        testId="doc-editor-dialog"
        maxWidth="3xl"
      >
        {editorType && editorArtifact && (
          <DocumentEditor
            projectId={projectId}
            docType={editorType}
            artifact={editorArtifact}
            generationNotes={generationNotes}
            fastTrack={editorType ? isCommunicationItem(editorType) : false}
            onSend={(type) => {
              setEditorType(null);
              void openSend(type);
            }}
            onClose={() => setEditorType(null)}
          />
        )}
      </Dialog>

      {/* Universal send-email dialog (client_followup / sourcing / kickoff).
          Pre-selects the project's already-assigned sourcing team (E03-F08-T04 /
          ISSUE-4) so the "Send to Sourcing Team" popup does not force a re-pick. */}
      {send && (
        <SendEmailDialog
          projectId={projectId}
          itemKey={send.itemKey}
          open
          onClose={() => setSend(null)}
          title={send.title}
          initialTo={send.to}
          initialCc={send.cc}
          initialBcc={send.bcc ?? []}
          initialSubject={send.subject}
          initialBody={send.body}
          initialSourcingTeam={sourcingTeam}
          onSent={() => setSend(null)}
        />
      )}

      {/* Internal notification («Kickoff done mail») dialog. The card's Sent state
          comes from `project.internal_notification_sent_at`, refreshed by the
          send mutation's project invalidation (E03-F07-T07). */}
      <InternalNotificationDialog
        projectId={projectId}
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        templateVars={notifTemplateVars}
        onSent={() => setNotifyOpen(false)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Stage-4: Launched body — "Package Ready for Delivery" (FP-01 / E02-F07-T01)
// ---------------------------------------------------------------------------

/**
 * The delivery surface host: mounts `LaunchedStageView` and owns the read-only
 * preview dialog, the `.doc` export, and the "Send Final Package" flow. PK-QA-36 —
 * the preview renders `DocumentView` DIRECTLY (not `DocumentEditor`), so Launched
 * is a strictly read-only, TERMINAL archive: no Edit toggle, no Regenerate, no
 * Save/Approve footer (all of which `DocumentEditor` renders unconditionally). To
 * change a doc the recruiter sends the project back to Generation via the Back
 * nav, where the status-based gating already re-enables editing.
 *
 * "Send Final Package" opens the generic `SendEmailDialog`
 * (`itemKey='final_package'`) pre-composed with the NAMES of the generated
 * documents (E05-F06-T11). The documents themselves travel as `.docx`
 * attachments built server-side at send time — the previous flow pasted one
 * presigned R2 url per document into the body (long, and dead after the presign
 * expiry). Recipients remembered from the last successful package send are
 * restored (KS3-05). Replaces the mis-mounted `ResearchLibraryView` (audit P0-1).
 */
function LaunchedStageBody({
  projectId,
  meta,
  artifactMap,
  savedRecipients,
}: {
  projectId: number;
  meta: GenerationProjectMeta;
  artifactMap: ArtifactMap;
  savedRecipients: EmailRecipientsMap;
}): JSX.Element {
  const toast = useToast();
  const [editorType, setEditorType] = useState<EditableDocType | null>(null);
  const [send, setSend] = useState<SendState | null>(null);

  const onDownload = useCallback(
    (docType: string) => {
      const ok = downloadArtifactDoc({
        docType,
        content: artifactMap[docType]?.content ?? null,
        clientName: meta.clientName,
        roleName: meta.roleName,
      });
      if (!ok) toast.error('No content to download');
    },
    [artifactMap, meta.clientName, meta.roleName, toast],
  );

  /**
   * "Send Final Package" — open the generic SendEmailDialog pre-composed with a
   * short list of the attached documents (no urls). The server attaches every
   * generated package document on send (`shouldAttachPackage`), so the body only
   * names them. Documents with no row / empty content are not listed — the
   * server skips the same ones.
   */
  const onSendPackage = useCallback(() => {
    const labels = GENERATION_CARDS.filter((t) => {
      const content = artifactMap[t]?.content;
      if (content == null) return false;
      return !(typeof content === 'object' && Object.keys(content as object).length === 0);
    }).map((t) => exportLabel(t));
    const saved = savedRecipients['final_package'];
    const savedTo = (saved?.to ?? []).filter((e) => e.trim().length > 0);
    setSend({
      itemKey: 'final_package',
      title: 'Send Final Package',
      to: savedTo.length > 0 ? savedTo.join(', ') : meta.contactEmail,
      subject: `Final Package: ${meta.clientName} - ${meta.roleName}`,
      body: finalPackageBody(labels),
      cc: (saved?.cc ?? []).filter((e) => e.trim().length > 0),
      bcc: (saved?.bcc ?? []).filter((e) => e.trim().length > 0),
    });
  }, [artifactMap, savedRecipients, meta.contactEmail, meta.clientName, meta.roleName]);

  const editorArtifact = editorType ? artifactMap[editorType] : undefined;
  const previewStatus = editorType ? artifactStatusOf(artifactMap, editorType) : 'not_generated';
  const previewBadge = badgeFor(previewStatus);

  return (
    <>
      <LaunchedStageView
        artifacts={artifactMap}
        onPreview={(type) => setEditorType(type as EditableDocType)}
        onDownload={onDownload}
        onSendPackage={onSendPackage}
      />

      {/* Read-only preview — DocumentView rendered DIRECTLY (no editor chrome:
          no Edit toggle, no Regenerate, no Save/Approve), per PK-QA-36. */}
      <Dialog
        open={editorType !== null}
        onClose={() => setEditorType(null)}
        title="Document"
        testId="launched-doc-dialog"
        maxWidth="3xl"
      >
        {editorType && editorArtifact && (
          <div data-testid="launched-doc-preview" className="flex flex-col gap-3">
            {/* Read-only header: doc title + status badge (no mutating actions). */}
            <div className="flex items-center justify-between gap-2">
              <h3
                data-testid="launched-doc-title"
                className="font-semibold text-[var(--color-text-strong)]"
              >
                {GENERATION_LABELS[editorType]}
              </h3>
              <span
                data-testid="launched-doc-status"
                data-status={previewStatus}
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${previewBadge.toneClass}`}
              >
                {previewBadge.label}
              </span>
            </div>

            <DocumentView docType={editorType} content={editorArtifact.content} />

            {/* Close — a terminal read-only archive has no Save/Approve footer. */}
            <div className="flex justify-end border-t border-[var(--color-border)] pt-3">
              <button
                type="button"
                data-testid="launched-doc-close"
                onClick={() => setEditorType(null)}
                className="min-h-[36px] px-4 rounded-md border border-[var(--color-border)] text-sm hover:bg-[var(--color-surface-muted)]"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Send Final Package — the generic send dialog pre-composed with the
          attached-document list (itemKey='final_package', E05-F06-T11). */}
      {send && (
        <SendEmailDialog
          projectId={projectId}
          itemKey={send.itemKey}
          open
          onClose={() => setSend(null)}
          title={send.title}
          initialTo={send.to}
          initialCc={send.cc}
          initialBcc={send.bcc ?? []}
          initialSubject={send.subject}
          initialBody={send.body}
          onSent={() => setSend(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Stage-advance controls (legacy advanceStage Back / Next)
// ---------------------------------------------------------------------------

function StageNav({
  stage,
  onAdvance,
  busy,
}: {
  stage: number;
  onAdvance: (stage: number) => void;
  busy: boolean;
}): JSX.Element | null {
  const showBack = stage > MIN_STAGE;
  const showNext = stage < MAX_NAV_STAGE;
  if (!showBack && !showNext) return null;
  return (
    <div
      data-testid="project-stage-nav"
      className="flex items-center justify-between border-t border-border pt-4"
    >
      {showBack ? (
        <button
          type="button"
          data-testid="project-stage-back"
          disabled={busy}
          onClick={() => onAdvance(stage - 1)}
          className="min-h-[40px] px-4 rounded-md border border-border text-sm hover:bg-surface-muted disabled:opacity-60"
        >
          <i className="fa-solid fa-arrow-left mr-2" aria-hidden />
          Back: {STAGE_NAMES[stage - 1] ?? ''}
        </button>
      ) : (
        <span />
      )}
      {showNext ? (
        <button
          type="button"
          data-testid="project-stage-next"
          disabled={busy}
          onClick={() => onAdvance(stage + 1)}
          className="min-h-[40px] px-4 rounded-md bg-primary text-white text-sm hover:bg-primary-hover disabled:opacity-60"
        >
          Next: {STAGE_NAMES[stage + 1] ?? ''}
          <i className="fa-solid fa-arrow-right ml-2" aria-hidden />
        </button>
      ) : (
        <span />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main view
// ---------------------------------------------------------------------------

export function ProjectDetailView({ projectId, onClose }: ProjectDetailViewProps): JSX.Element {
  const query = useProjectDetail(projectId);
  const updateProject = useUpdateProject();
  const advanceMutation = useAdvanceStage();
  const toast = useToast();
  const queryClient = useQueryClient();

  // E12-F17-T15 — an abandoned "New Project" draft must not linger in the list.
  // Leaving the card (any navigation away, or switching project) schedules the
  // discard of a still-fresh draft; the server only deletes it if it is still
  // untouched. The macrotask defer + cancel-on-mount absorbs StrictMode's double
  // effect run (mount → unmount → mount is not a real leave).
  useEffect(() => {
    if (projectId === null) return undefined;
    const id = projectId;
    cancelScheduledDiscard(id);
    return () => {
      scheduleDraftDiscard(id, () => {
        void discardProjectDraft(id).then((deleted) => {
          if (deleted) void queryClient.invalidateQueries({ queryKey: qk.projects.lists() });
        });
      });
    };
  }, [projectId, queryClient]);

  /** Cancel on a fresh draft = abandon the create: discard it and go back. */
  const onDiscardDraft = useCallback(async (): Promise<void> => {
    if (projectId === null) return;
    forgetFreshDraft(projectId);
    const deleted = await discardProjectDraft(projectId);
    if (!deleted) return; // the user already put work into it — keep the card
    void queryClient.invalidateQueries({ queryKey: qk.projects.lists() });
    toast.success('New project discarded');
    onClose?.();
  }, [projectId, queryClient, toast, onClose]);
  // PK-QA-1 — shared confirm dialog for the `no_analysis_yet` soft gate on the
  // Pre-Kickoff(1)→Kick-off(2) advance (same styled hook the panels use).
  const { confirm, confirmDialog } = useConfirm();

  // The stage currently PREVIEWED in the stepper (0-based step index, null =
  // viewing the project's real current stage). Never mutates project.stage.
  const [previewStep, setPreviewStep] = useState<number | null>(null);

  // PK-QA-1 UX — the header form-field keys the Pre-Kickoff analysis 400
  // VALIDATION_ERROR reported as missing (client_name / role_name / contact_email).
  // Lifted HERE (the shared parent of the panel that raises it and the header that
  // renders the fields) so a failed "AI Analysis" can outline the actual inputs on
  // the header, not just toast their labels. Set by PreKickoffPanel's
  // onValidationMissing; cleared on a successful run (empty set) or a header Save.
  const [missingFields, setMissingFields] = useState<ReadonlySet<string>>(() => new Set());

  // B2/KS-09 — the Stage-1 "Send draft" handoff. Without this prop PreKickoffPanel
  // renders the editor but HIDES its Send button, which is why the button Ksenia
  // was looking for did not exist in the app at all. The mutation saves the edited
  // draft before sending it (see saveAndSendPreKickoffDraft — the server mails the
  // STORED artifact, so an unsaved edit would be silently dropped).
  const sendPreKickoff = useSendPreKickoffDraft(projectId ?? 0);
  const onSendPreKickoffDraft = useCallback(
    (option: PreKickoffDraftOption, draft: DraftEditorState) => {
      if (projectId === null) return;
      void (async () => {
        try {
          await sendPreKickoff.mutateAsync({ option, draft });
          toast.success('Pre-Kickoff email sent');
        } catch (error) {
          // PK-QA-29 — show the REAL reason (404 no draft / 503 no sender mailbox /
          // 502 transport refused), never a generic failure string.
          toast.error(errorMessage(error, 'Failed to send the Pre-Kickoff email'));
        }
      })();
    },
    [projectId, sendPreKickoff, toast],
  );

  const onSaveGenerationNotes = useCallback(
    (notes: string) => {
      if (projectId === null) return;
      void updateProject.mutate({
        projectId,
        updates: { generation_notes: notes },
      });
    },
    [updateProject, projectId],
  );

  /**
   * Advance / retreat the project stage (POST /:id/advance). Two GATES on the
   * Pre-Kickoff(1)→Kick-off(2) move, checked in the SAME order the server checks
   * them (fail-fast, hard-before-soft):
   *   - PK-QA-8: a hard, NON-overridable 400 `VALIDATION_ERROR` when the project
   *     is missing client / role / contact-email. There is NO confirm path — reuse
   *     the shared missing-field highlight (`setMissingFields`, the exact state the
   *     AI-analysis 400 already feeds via `onValidationMissing`) so the header
   *     outlines the actual inputs + auto-opens the contact block, pin a sticky
   *     toast, and STOP. Fires even when `confirmed:true` (the invariant is not a
   *     nudge), so it is read first.
   *   - PK-QA-1/PK-QA-3: a soft 412 `CONFIRM_REQUIRED(no_analysis_yet)` (no
   *     analysis artifact yet) — offer the confirm and, on "Continue", resend with
   *     `confirmed:true`. `confirmed` guards the recursion so a second 412 never
   *     loops.
   * And one GATE on the Kick-off(2)→Generation(3) move:
   *   - PK-QA-20: a hard, NON-overridable 400 `KICKOFF_INCOMPLETE` when the meeting
   *     is not completed and/or no AI Processing (kickoff enrichment) has run.
   *     There is NO confirm/override path — just block and toast (copy tailored to
   *     which of the two conditions is unmet). Distinct code from the 1→2
   *     `VALIDATION_ERROR` so the two hard 400s never cross-fire.
   * Any other failure is a generic toast (never rethrows — the "Confirm & Move to
   * Final" caller awaits this).
   */
  const advanceStage = useCallback(
    async (nextStage: number): Promise<void> => {
      if (projectId === null) return;

      /**
       * KS3-01 — a BACKWARD move asks first.
       *
       * Ксения: «при переключении стадий назад обязательно сделать предупреждение
       * о возможном сбросе». The server does not wipe anything on a Back (the
       * stage column is the only thing that changes), and the copy says so — a
       * warning that overstates the danger teaches people to click through it.
       * What it does warn about is real: the later stage has to be walked through
       * again, and the gates on the way forward (completed meeting, AI Processing)
       * apply anew — re-running a generation REPLACES the current documents.
       *
       * Asked BEFORE the request, unlike the server-driven `no_analysis_yet`
       * confirm below: there is no failure to react to here, the move would just
       * silently succeed.
       */
      const currentStage = query.data?.project.stage ?? null;
      if (currentStage !== null && nextStage < currentStage) {
        const target = STAGE_NAMES[nextStage] ?? `stage ${nextStage}`;
        const ok = await confirm({
          title: `Move back to ${target}?`,
          message:
            'Nothing is deleted — documents, answers and sent emails stay as they are. ' +
            'But this stage will have to be completed again, and re-running a generation ' +
            'replaces the current documents. Continue?',
          confirmLabel: 'Move back',
          cancelLabel: 'Stay here',
          testId: 'project-stage-back-confirm',
        });
        if (!ok) return;
      }

      const attempt = async (confirmed: boolean): Promise<void> => {
        try {
          await advanceMutation.mutateAsync({ projectId, stage: nextStage, confirmed });
          setPreviewStep(null);
          toast.success(`Project moved to ${STAGE_NAMES[nextStage] ?? nextStage} stage`);
          if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error) {
          const body = errorBodyOf(error);
          // PK-QA-8 — hard 400 invariant FIRST (same parser as PreKickoffPanel).
          // Non-overridable: highlight + sticky toast, never a confirm/retry.
          const missing = readValidationFields(body);
          if (missing && missing.length > 0) {
            setMissingFields(new Set(missing));
            const labels = missing.map((f) => MISSING_FIELD_LABELS[f] ?? f).join(', ');
            toast.error(`Add the required details first: ${labels}`);
            return;
          }
          // PK-QA-20 — hard 400 `KICKOFF_INCOMPLETE` on the Kick-off(2)→Generation(3)
          // move: the meeting must be completed AND at least one AI Processing
          // (kickoff enrichment) run first. Non-overridable — block + toast, no
          // confirm/retry. Tailor the copy to which of the two conditions is unmet.
          const kickoffIncomplete = readKickoffIncompleteFields(body);
          if (kickoffIncomplete) {
            const needMeeting = kickoffIncomplete.includes('meeting_completed');
            const needEnrichment = kickoffIncomplete.includes('kickoff_enrichment');
            const message =
              needMeeting && needEnrichment
                ? 'Complete the meeting and run AI Processing before moving to Generation'
                : needMeeting
                  ? 'Complete the meeting before moving to Generation'
                  : needEnrichment
                    ? 'Run AI Processing before moving to Generation'
                    : 'Complete the Kick-off steps before moving to Generation';
            toast.error(message);
            return;
          }
          if (!confirmed && isConfirmRequired(body) === 'no_analysis_yet') {
            const ok = await confirm({
              title: 'Advance stage?',
              message: 'The analysis has not been run yet. Move to the next stage anyway?',
              confirmLabel: 'Continue',
              cancelLabel: 'Cancel',
              testId: 'project-advance-confirm',
            });
            if (ok) await attempt(true);
            return;
          }
          toast.error('Error changing stage');
        }
      };
      await attempt(false);
    },
    [projectId, advanceMutation, toast, confirm, setMissingFields, query.data],
  );

  if (projectId === null) {
    return (
      <div
        data-testid="project-detail-empty"
        className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-text-muted"
      >
        Select a project to open its detail card.
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div data-testid="project-detail-loading" className="p-8 text-sm text-text-muted">
        <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
        Loading project…
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div
        data-testid="project-detail-not-found"
        className="rounded-lg border border-red-200 bg-red-50 p-8 text-center text-sm text-red-700"
      >
        Project not found or out of scope.
      </div>
    );
  }

  const card = query.data;
  const header = headerFields(card.project);
  // The step the body shows: the previewed step when set, else the current stage.
  const currentStep = stageToStepIndex(card.project.stage);
  const shownStep = previewStep ?? currentStep;
  const realStage = Math.min(Math.max(card.project.stage ?? MIN_STAGE, MIN_STAGE), MAX_NAV_STAGE);

  // The persisted Pre-Kickoff artifacts (T02 UPSERT rows) — `DetailArtifact` is
  // `{ type, content, … }`, exactly the `PersistedArtifactRow` the panel hydrates.
  const artifacts: readonly PersistedArtifactRow[] = card.artifacts;

  // Artifact map keyed by type for the Generation stage.
  const artifactMap: ArtifactMap = Object.fromEntries(card.artifacts.map((a) => [a.type, a]));

  // generation_notes from projects.data (optional string, default '').
  const generationNotes =
    typeof card.project.data['generation_notes'] === 'string'
      ? card.project.data['generation_notes']
      : '';

  const genMeta: GenerationProjectMeta = {
    clientName: header.clientName === '—' ? '' : header.clientName,
    roleName: header.roleName === 'New Position' ? '' : header.roleName,
    contactEmail: card.project.contact_email ?? '',
  };

  // KS3-05 — addressees remembered per sent document (projects.data.email_recipients).
  // Read defensively: the key is absent on every project that predates the feature.
  const savedRecipients: EmailRecipientsMap =
    ((card.project.data as Record<string, unknown> | null)?.['email_recipients'] as
      | EmailRecipientsMap
      | undefined) ?? {};

  // PK-QA-26 — the FULL snake_case notification template-var set, built from the
  // project card + kickoff_enrichment + signed-in user (client-side, variant (a)).
  const notifTemplateVars = buildNotifTemplateVarsClient(
    card.project,
    genMeta.clientName,
    genMeta.roleName,
    getCachedUser()?.name ?? '',
  );

  return (
    <div data-testid="project-detail-view" className="space-y-6">
      {/* Editable 3-column header (role / client / contact) + status badge +
          Save/Cancel — the T03/T05/T06 inline-edit card (ProjectDetailHeader).
          Keyed by project id so re-opening another project re-seeds the form. */}
      <ProjectDetailHeader
        key={card.project.id}
        project={card.project}
        missingFields={missingFields}
        onClearMissingFields={() => setMissingFields(new Set())}
        onDiscardDraft={isFreshDraft(card.project.id) ? () => void onDiscardDraft() : undefined}
      />

      {/* Stage stepper — current highlight + preview banner */}
      <StageStepper
        stage={card.project.stage}
        onStageView={setPreviewStep}
        testId="project-detail-stage"
      />

      {/* Per-stage body. Stage index is 0-based:
          0 = Pre-Kickoff, 1 = Kick-off, 2 = Generation, 3 = Launched. */}
      <section data-testid="project-detail-stage-body">
        {shownStep === 0 ? (
          <SectionErrorBoundary label="Pre-Kickoff" resetKey={shownStep}>
            {/* KS-02 — the addressee picker sits in Stage 1: this is where the
                client-facing pre-kickoff emails are drafted and sent, so it is where
                "who receives them" has to be answerable. */}
            <div className="mb-4">
              <ProjectContactsCard
                projectId={card.project.id}
                clientId={card.project.client_id ?? null}
              />
            </div>
            <PreKickoffPanel
              projectId={card.project.id}
              initialArtifacts={artifacts}
              project={{ clientName: header.clientName, roleName: header.roleName }}
              onValidationMissing={(fields) => setMissingFields(new Set(fields))}
              onSend={onSendPreKickoffDraft}
            />
          </SectionErrorBoundary>
        ) : shownStep === 1 ? (
          <SectionErrorBoundary label="Kick-off" resetKey={shownStep}>
            <KickoffStageBody
              projectId={card.project.id}
              projectRoleName={card.project.role_name}
              projectData={card.project.data}
            />
          </SectionErrorBoundary>
        ) : shownStep === 2 ? (
          <SectionErrorBoundary label="Generation" resetKey={shownStep}>
            <GenerationStageBody
              projectId={card.project.id}
              meta={genMeta}
              artifactMap={artifactMap}
              projectData={card.project.data}
              generationNotes={generationNotes}
              sourcingTeamId={card.project.sourcing_team_id ?? null}
              sourcingTeam={card.project.sourcing_team ?? null}
              savedRecipients={savedRecipients}
              notifTemplateVars={notifTemplateVars}
              notificationSentAt={card.project.internal_notification_sent_at ?? null}
              onSaveGenerationNotes={onSaveGenerationNotes}
              onAdvanceStage={advanceStage}
            />
          </SectionErrorBoundary>
        ) : (
          <SectionErrorBoundary label="Launched" resetKey={shownStep}>
            <LaunchedStageBody
              projectId={card.project.id}
              meta={genMeta}
              artifactMap={artifactMap}
              savedRecipients={savedRecipients}
            />
          </SectionErrorBoundary>
        )}
      </section>

      {/* Stage-advance controls (Back / Next) — uses the REAL stage, not the
          previewed one (legacy advanceStage nav). */}
      <StageNav stage={realStage} onAdvance={advanceStage} busy={advanceMutation.isPending} />

      {/* Project event history */}
      <section
        data-testid="project-detail-timeline"
        className="rounded-lg border border-border p-4"
      >
        <ProjectTimeline projectId={card.project.id} />
      </section>

      {/* PK-QA-1 — styled confirm for the `no_analysis_yet` advance soft gate. */}
      {confirmDialog}
    </div>
  );
}

export default ProjectDetailView;
